// MapComponent.js
import React, { useState, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip,
  Circle,
  Polyline
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import PopupContent from './PopupContent';
import L from 'leaflet';

// Иконки
import iconTruck  from './img/icons8-truck-48.png';
import iconBlue   from './img/marker-icon-2x-blue.png';
import iconGreen  from './img/marker-icon-2x-green.png';
import iconRed    from './img/marker-icon-2x-red.png';
import iconOrange from './img/marker-icon-2x-orange.png';
import iconYellow from './img/marker-icon-2x-yellow.png';
import iconGrey   from './img/marker-icon-2x-grey.png';
import iconViolet from './img/marker-icon-2x-violet.png';
import iconGold   from './img/marker-icon-2x-gold.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const statusIcons = {
  'Открыта':       iconBlue,
  'Закрыта':       iconGreen,
  'Не доставлено': iconRed,
  'Не доставлен':  iconRed,
  'В процессе':    iconOrange,
  'Ожидает':       iconYellow,
};

const truckIcon = new L.Icon({
  iconUrl: iconTruck,
  iconSize:   [48, 48],
  iconAnchor: [24, 24],
  popupAnchor:[0, -24],
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

export default function MapComponent({
  center,
  zoom,
  receipts,
  onMarkerClick,
  userLocation,
  allowedUsersLocations,
  highlightPosition,
  courierLocations = []
}) {
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [showFullRoute, setShowFullRoute] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);

  const formatDiff = (ms) => {
    if (!Number.isFinite(ms)) return '';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const parts = [];
    if (h > 0) parts.push(`${h}ч`);
    if (m > 0) parts.push(`${m}м`);
    parts.push(`${s}с`);
    return parts.join(' ');
  };

  const formatAgo = (whenMs) => {
    if (!Number.isFinite(whenMs)) return '';
    const diff = Date.now() - whenMs;
    const sec = Math.max(0, Math.floor(diff / 1000));
    if (sec < 60) return `${sec} с назад`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} мин назад`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} ч назад`;
    const d = Math.floor(hr / 24);
    return `${d} дн назад`;
  };

  // ✅ Квитанции: нормализуем координаты и фильтруем
  const receiptsWithCoords = useMemo(() => {
    return (Array.isArray(receipts) ? receipts : [])
      .map(r => {
        const lat = Number(r.latitude);
        const lng = Number(r.longitude);
        return { ...r, latitude: lat, longitude: lng };
      })
      .filter(r => Number.isFinite(r.latitude) && Number.isFinite(r.longitude));
  }, [receipts]);

  // ✅ Курьеры: lat/lng + timestamp → updatedAtMs
  const safeCouriers = useMemo(() => {
    return (Array.isArray(courierLocations) ? courierLocations : [])
      .map(c => {
        const lat = Number(c.latitude ?? c.lat ?? c.coords?.lat ?? c.location?.lat);
        const lng = Number(c.longitude ?? c.lng ?? c.coords?.lng ?? c.location?.lng);
        // время от бэка: timestamp (мс). Фолбэки на всякий.
        const tsRaw = c.updatedAtMs ?? c.recordedAt ?? c.timestamp ?? c.time ?? null;
        let tsMs = null;
        if (tsRaw != null) {
          const n = Number(tsRaw);
          if (Number.isFinite(n)) tsMs = n < 1e12 ? n * 1000 : n; // вдруг секундами
          else {
            const p = Date.parse(String(tsRaw));
            if (Number.isFinite(p)) tsMs = p;
          }
        }
        return { ...c, latitude: lat, longitude: lng, updatedAtMs: tsMs };
      })
      .filter(c => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));
  }, [courierLocations]);

  // Цвет статуса
  const getStatusColor = status => {
    switch (status) {
      case 'Открыта': return 'blue';
      case 'Закрыта': return 'green';
      case 'Не доставлено':
      case 'Не доставлен': return 'red';
      case 'В процессе': return 'orange';
      case 'Ожидает': return 'yellow';
      default: return 'gray';
    }
  };

  // Нумерация точек по времени
  const orderMap = useMemo(() => {
    if (!showStatusHistory) return {};
    return receiptsWithCoords
      .filter(r => r.status_changed_at)
      .slice()
      .sort((a, b) => new Date(a.status_changed_at) - new Date(b.status_changed_at))
      .reduce((acc, rec, idx) => {
        acc[rec.id] = idx + 1;
        return acc;
      }, {});
  }, [receiptsWithCoords, showStatusHistory]);

  // История для линии и дельт
  const history = useMemo(() => {
    if (!showStatusHistory) return [];
    return receiptsWithCoords
      .filter(r => r.status_changed_at)
      .slice()
      .sort((a, b) => new Date(a.status_changed_at) - new Date(b.status_changed_at));
  }, [receiptsWithCoords, showStatusHistory]);

  // Полный маршрут
  const fullPathPositions = useMemo(() => {
    return receiptsWithCoords
      .filter(r => r.status_changed_at)
      .sort((a, b) => new Date(a.status_changed_at) - new Date(b.status_changed_at))
      .map(r => [r.latitude, r.longitude]);
  }, [receiptsWithCoords]);

  // Дельты между соседями
  const deltaMap = useMemo(() => {
    const deltas = {};
    for (let i = 1; i < history.length; i++) {
      const prev = history[i - 1];
      const cur  = history[i];
      deltas[cur.id] = new Date(cur.status_changed_at) - new Date(prev.status_changed_at);
    }
    return deltas;
  }, [history]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* Кнопки */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: 'rgba(255,255,255,0.9)',
          padding: 6,
          borderRadius: 4,
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
        }}
      >
        <button onClick={() => setShowFullRoute(f => !f)}>
          {showFullRoute ? 'Скрыть маршрут' : 'Показать маршрут'}
        </button>
        <button onClick={() => setShowStatusHistory(h => !h)} style={{ marginLeft: 8 }}>
          {showStatusHistory ? 'Скрыть историю' : 'Показать историю'}
        </button>
      </div>

      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />

        {/* ✅ Курьеры — один раз, безопасно */}
        <MarkerClusterGroup showCoverageOnHover={false} spiderfyOnMaxZoom chunkedLoading>
          {safeCouriers.map(loc => {
            const latText = Number.isFinite(loc.latitude) ? loc.latitude.toFixed(6) : '-';
            const lngText = Number.isFinite(loc.longitude) ? loc.longitude.toFixed(6) : '-';
            return (
              <Marker
                key={`courier-${loc.courierId ?? `${latText}-${lngText}`}`}
                position={[loc.latitude, loc.longitude]}
                icon={truckIcon}
              >
                <Popup>
                  <div>
                    <strong>Курьер:</strong> {loc.firstName} {loc.lastName} {loc.username ? `(@${loc.username})` : ''}<br/>
                    {loc.courierId && (<><strong>ID:</strong> {loc.courierId}<br/></>)}
                    <strong>Широта:</strong> {latText}<br/>
                    <strong>Долгота:</strong> {lngText}<br/>
                    {Number.isFinite(loc.updatedAtMs)
                      ? (<>
                          <strong>Обновлено:</strong> {new Date(loc.updatedAtMs).toLocaleString('ru-RU')}
                          {' '}({formatAgo(loc.updatedAtMs)})
                        </>)
                      : (<em>Время не передано</em>)
                    }
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>

        {/* Полный маршрут пунктиром */}
        {showFullRoute && fullPathPositions.length > 1 && (
          <Polyline positions={fullPathPositions} pathOptions={{ color: 'blue', dashArray: '5' }} />
        )}

        {/* Маркеры квитанций */}
        {receiptsWithCoords.map(receipt => {
          const iconUrl = statusIcons[receipt.status] || iconGrey;
          const iconObj = new L.Icon({
            iconUrl, shadowUrl: iconShadow,
            iconSize: [25,41], iconAnchor: [12,41],
            popupAnchor: [1,-34], shadowSize: [41,41]
          });

          return (
            <Marker
              key={receipt.id}
              position={[receipt.latitude, receipt.longitude]}
              icon={iconObj}
              eventHandlers={onMarkerClick ? { click: () => onMarkerClick(receipt.id) } : undefined}
            >
              {/* Номер в истории */}
              {showStatusHistory && orderMap[receipt.id] && (
                <Tooltip
                  direction="top"
                  offset={[0, -35]}
                  permanent
                  interactive
                  eventHandlers={{ click: () => setHighlightedId(receipt.id) }}
                  className="status-order-tooltip"
                >
                  <span style={{ cursor: 'pointer' }}>{orderMap[receipt.id]}</span>
                </Tooltip>
              )}

              {!showStatusHistory && (
                <Popup>
                  <PopupContent
                    receipt={receipt}
                    getStatusColor={getStatusColor}
                    orderNumber={orderMap[receipt.id]}
                    delta={deltaMap[receipt.id]}
                  />
                </Popup>
              )}
            </Marker>
          );
        })}

        {/* Выделение круга */}
        {Array.isArray(highlightPosition)
          && Number.isFinite(+highlightPosition[0])
          && Number.isFinite(+highlightPosition[1]) && (
          <Circle center={highlightPosition} radius={50} color="red" fillColor="red" fillOpacity={0.3} />
        )}

        {/* Точка «Вы здесь» */}
        {Array.isArray(userLocation)
          && Number.isFinite(+userLocation[0])
          && Number.isFinite(+userLocation[1]) && (
          <Marker
            position={userLocation}
            icon={new L.Icon({
              iconUrl: iconViolet, shadowUrl: iconShadow,
              iconSize: [25,41], iconAnchor: [12,41],
              popupAnchor: [1,-34], shadowSize: [41,41]
            })}
          >
            <Popup>Вы здесь</Popup>
          </Marker>
        )}

        {/* Маркеры разрешённых пользователей */}
        {allowedUsersLocations && Object.entries(allowedUsersLocations).map(([id, raw]) => {
          const lat = Number(raw?.lat);
          const lng = Number(raw?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return (
            <Marker
              key={`allowed-${id}`}
              position={[lat, lng]}
              icon={new L.Icon({
                iconUrl: iconGold, shadowUrl: iconShadow,
                iconSize: [25,41], iconAnchor: [12,41],
                popupAnchor: [1,-34], shadowSize: [41,41]
              })}
            >
              <Popup>Пользователь {id}</Popup>
            </Marker>
          );
        })}

        {/* Линия и дельта для выделенной точки */}
        {highlightedId != null && (() => {
          const idx = history.findIndex(r => r.id === highlightedId);
          if (idx > 0) {
            const prev = history[idx - 1];
            const cur  = history[idx];
            const coords = [
              [prev.latitude, prev.longitude],
              [cur.latitude,  cur.longitude ]
            ];
            const mid = [
              (prev.latitude + cur.latitude) / 2,
              (prev.longitude + cur.longitude) / 2
            ];
            return (
              <>
                <Polyline positions={coords} pathOptions={{ color: 'blue', dashArray: '5' }} />
                <Marker
                  position={mid}
                  icon={L.divIcon({
                    className: 'delta-label',
                    html: `<div style="
                      min-width: 60px;
                      white-space: nowrap;
                      background: white;
                      padding: 4px 8px;
                      border: 1px solid #777;
                      border-radius: 3px;
                      font-size: 12px;
                      text-align: center;
                    ">
                      ${formatDiff(deltaMap[cur.id])}
                    </div>`
                  })}
                />
              </>
            );
          }
          return null;
        })()}
      </MapContainer>
    </div>
  );
}
